import React, { useState, ChangeEvent, SyntheticEvent } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';

import { useBoolModalState } from '@app/core';
import { ParticipationTag, Participation } from '@app/api';
import { Input, Chip, CenteredModal, Button, useToast } from 'components';
import { Trans, useTranslation } from 'react-i18next';
import { getNextTagColor } from 'lib/tags';
import {
  useSession,
  useUpdateParticipationTags,
  useAddAvailableTag,
} from 'queries';

const useStyles = createUseStyles((theme: Theme) => ({
  tagList: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  tagContainer: {
    flex: 1,
    minWidth: 100,
    marginBottom: theme.marginBase,
    textAlign: 'left',
  },
  empty: {
    ...theme.fonts.caption,
    color: theme.colors.inkTertiary,
    textAlign: 'center',
  },
  btnContainer: {
    textAlign: 'center',
  },
}));

interface Props {
  participation: Participation;
}

export const EditableParticipationTagList = ({ participation }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const toast = useToast();
  const { t } = useTranslation();

  const { data: session } = useSession(participation.sessionId);
  const tags = session ? session.availableTags || [] : [];

  const [addTagOpened, openTagModal, closeTagModal] = useBoolModalState();
  const selectedTags = participation.tags || [];
  const [tagName, setTagName] = useState('');

  const { mutate: updateTags } = useUpdateParticipationTags(participation.id);
  const { mutateAsync: addAvailableTag } = useAddAvailableTag(
    participation.sessionId,
  );

  const onAddClick = () => {
    setTagName('');
    openTagModal();
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTagName(e.currentTarget.value);
  };

  const toggleSelectedTag = (tag: ParticipationTag) => {
    if (!selectedTags.includes(tag.id)) {
      return updateTags({ tags: [...selectedTags, tag.id] });
    }
    return updateTags({ tags: selectedTags.filter((t) => t !== tag.id) });
  };

  const addTag = async (e: SyntheticEvent) => {
    e.preventDefault();
    try {
      const name = tagName;
      if (!name) {
        return;
      }
      const color = getNextTagColor(tags);
      const tag = await addAvailableTag({ name, color });
      toggleSelectedTag(tag);
    } catch (e) {
      toast.exception(e);
    }
    closeTagModal();
  };

  return (
    <>
      <div className={classes.tagList}>
        {tags &&
          tags.map((tag) => (
            <button
              className={classes.tagContainer}
              onClick={() => toggleSelectedTag(tag)}
            >
              <Chip
                customColor={tag.color}
                key={tag.id}
                variant="selectable"
                selected={selectedTags.includes(tag.id)}
              >
                {tag.name}
              </Chip>
            </button>
          ))}
      </div>
      {!tags ||
        (tags.length === 0 && (
          <div className={classes.empty}>
            <Trans i18nKey="sessions.details.monitoring.tags.empty" />
          </div>
        ))}

      <div className={classes.btnContainer}>
        <Button onClick={onAddClick} variant="transparent" color="accent">
          <Trans i18nKey="sessions.details.monitoring.tags.add" />
        </Button>
      </div>

      <CenteredModal
        onRequestClose={closeTagModal}
        isOpen={addTagOpened}
        title={t('sessions.details.monitoring.tags.add')}
        buttons={
          <Button color="green" onClick={addTag} disabled={!tagName}>
            <Trans i18nKey="sessions.details.monitoring.tags.confirm" />
          </Button>
        }
      >
        <form onSubmit={addTag}>
          <Input
            label={t('sessions.details.monitoring.tags.name')}
            onChange={onInputChange}
            value={tagName}
          />
        </form>
      </CenteredModal>
    </>
  );
};
